import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { LoadingCount } from "@volca/ui/components/ui/loading-count";
import {
  PageContainer,
  PageHeader,
  PageMainCard,
  PageMainStack,
} from "@volca/ui/components/ui/page-components";
import {
  Popover,
  PopoverContent,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
} from "@volca/ui/components/ui/popover";
import { SearchInput } from "@volca/ui/components/ui/search-input";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import {
  Tabs,
  TabsTrigger,
  TabsList,
  TabsContent,
} from "@volca/ui/components/ui/tabs";
import { Text } from "@volca/ui/components/ui/text";
import {
  SETTINGS_PAGE_TITLES,
  SIDEBAR_MODES,
} from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import type { BusinessSettingsSidebarSection } from "@/business-settings/context/business-settings-sidebar-context";
import { useDebounce } from "@/core/hooks";
import type { CustomerWithReferralsAndPayouts } from "@/customer/customer";
import { useGetCustomersV2 } from "@/customer/query";
import AddReferralModal from "@/referrals/common/add-referral-modal";
import { Advocates } from "./advocates";
import { CUSTOMER_TABS, CUSTOMER_TABS_LABELS } from "./constants";
import { Leads } from "./leads";

const Customers = () => {
  const { businessId, tab } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const {
    updateSidebar,
    state: { isOpen, selectedItemId },
  } = useBusinessSettingsSidebar();
  const [addReferralModalOpen, setAddReferralModalOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const debouncedSearchValue = useDebounce(searchValue, 300);

  const defaultTab = CUSTOMER_TABS.leads;
  const activeTab = tab ?? defaultTab;
  const activeCustomerType =
    activeTab === CUSTOMER_TABS.advocates
      ? CUSTOMER_TABS.advocates
      : CUSTOMER_TABS.leads;
  const sortByParam = searchParams.get("sort");
  const sortDirectionParam =
    searchParams.get("desc") === "true" ? "desc" : "asc";
  const sortBy =
    sortByParam === "name" ||
    sortByParam === "created_at" ||
    sortByParam === "referrals_count" ||
    sortByParam === "payouts_total"
      ? sortByParam
      : "created_at";
  const sortDirection = sortByParam ? sortDirectionParam : "desc";

  const customersQuery = useGetCustomersV2<CustomerWithReferralsAndPayouts>(
    businessId,
    {
      customerType: activeCustomerType,
      search: debouncedSearchValue,
      sortBy,
      sortDirection,
      withPayouts: true,
      withReferrals: true,
    },
  );

  useEffect(() => {
    if (!tab) {
      navigate(`/business-settings/${businessId}/customers/${defaultTab}`);
    }
  }, [tab, navigate, businessId, defaultTab]);

  const customers = useMemo(
    () => customersQuery.data?.pages.flatMap((page) => page.items) ?? [],
    [customersQuery.data],
  );

  const counts = customersQuery.data?.pages[0]?.counts;
  const leadCount = counts?.leads ?? 0;
  const advocateCount = counts?.advocates ?? 0;
  const isLoading = customersQuery.isLoading;
  const hasSearch = !!debouncedSearchValue.trim();
  const crossTabCount =
    activeTab === CUSTOMER_TABS.leads ? advocateCount : leadCount;
  const crossTabLabel =
    activeTab === CUSTOMER_TABS.leads
      ? CUSTOMER_TABS_LABELS[CUSTOMER_TABS.advocates]
      : CUSTOMER_TABS_LABELS[CUSTOMER_TABS.leads];
  const crossTabValue =
    activeTab === CUSTOMER_TABS.leads
      ? CUSTOMER_TABS.advocates
      : CUSTOMER_TABS.leads;
  const loadMoreLabel =
    activeTab === CUSTOMER_TABS.leads
      ? "Load more leads"
      : "Load more advocates";
  const scrollResetKey = `${activeCustomerType}:${debouncedSearchValue.trim()}:${sortBy}:${sortDirection}`;

  const handleNewCustomer = () => {
    if (activeTab === CUSTOMER_TABS.leads) {
      setAddReferralModalOpen(true);
    } else {
      updateSidebar({
        section: activeTab as BusinessSettingsSidebarSection,
        mode: SIDEBAR_MODES.new,
        selectedItemId: undefined,
        isOpen: true,
      });
    }
  };

  const handleTabChange = (tab: string) => {
    updateSidebar({
      selectedItemId: undefined,
      mode: SIDEBAR_MODES.view,
      section: activeTab as BusinessSettingsSidebarSection,
      isOpen: false,
    });
    navigate(`/business-settings/${businessId}/customers/${tab}`);
  };

  const bottomContent = customersQuery.hasNextPage ? (
    <Button
      variant="secondary"
      size="sm"
      onClick={() => customersQuery.fetchNextPage()}
      disabled={customersQuery.isFetchingNextPage}
      aria-busy={customersQuery.isFetchingNextPage || undefined}
    >
      {customersQuery.isFetchingNextPage && (
        <Spinner aria-hidden data-icon="inline-start" />
      )}
      {loadMoreLabel}
    </Button>
  ) : null;

  return (
    <>
      <PageContainer variant="workspace">
        <PageHeader
          title={SETTINGS_PAGE_TITLES.customers}
          actions={
            <Button variant="secondary" onClick={handleNewCustomer}>
              {activeTab === CUSTOMER_TABS.leads
                ? "Add referral"
                : "Add advocate"}
            </Button>
          }
        />
        <Tabs
          value={activeTab}
          onValueChange={handleTabChange}
          className="relative flex min-h-0 w-full flex-1 flex-col gap-4"
        >
          <TabsList>
            <TabsTrigger value={CUSTOMER_TABS.leads}>
              {CUSTOMER_TABS_LABELS[CUSTOMER_TABS.leads]}
              <LoadingCount
                count={leadCount}
                isCountLoading={isLoading}
                size="small"
              />
            </TabsTrigger>
            <TabsTrigger value={CUSTOMER_TABS.advocates}>
              {CUSTOMER_TABS_LABELS[CUSTOMER_TABS.advocates]}
              <LoadingCount
                count={advocateCount}
                isCountLoading={isLoading}
                size="small"
              />
            </TabsTrigger>
          </TabsList>
          <Popover>
            {/* Below the tabs on a phone; on the tab row's right from sm up. */}
            <PopoverTrigger
              render={
                <Button
                  variant="link"
                  className="h-8 self-start p-0 font-normal underline sm:absolute sm:top-0 sm:right-0"
                />
              }
            >
              What are leads and advocates?
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80 gap-4 p-4">
              <PopoverHeader>
                <PopoverTitle>Leads and advocates</PopoverTitle>
              </PopoverHeader>
              <Text>
                A <b>lead</b> is a person looking for service. An advocate of
                your business sent in their details. Once they're serviced, a
                lead can become an advocate.
              </Text>
              <Text>
                An <b>advocate</b> is a person who texted <b>Referral</b> to
                your business hotline and was given information on how to send
                in referrals. If they used a specific code to sign up, any
                referrals they send in are attributed to the business user that
                signed them up.
              </Text>
            </PopoverContent>
          </Popover>
          <PageMainCard>
            <PageMainStack className="min-h-0 flex-1 pt-6">
              <SearchInput
                placeholder="Search customers"
                value={searchValue}
                onChange={setSearchValue}
              />
              <TabsContent
                value={CUSTOMER_TABS.leads}
                className="min-h-0 w-full flex-1 overflow-hidden"
              >
                <Leads
                  customers={customers}
                  bottomContent={bottomContent}
                  isLoading={isLoading}
                  manualSorting
                  selectedCustomerId={isOpen ? selectedItemId : undefined}
                  scrollResetKey={scrollResetKey}
                />
              </TabsContent>
              <TabsContent
                value={CUSTOMER_TABS.advocates}
                className="min-h-0 w-full flex-1 overflow-hidden"
              >
                <Advocates
                  customers={customers}
                  bottomContent={bottomContent}
                  isLoading={isLoading}
                  manualSorting
                  selectedCustomerId={isOpen ? selectedItemId : undefined}
                  scrollResetKey={scrollResetKey}
                />
              </TabsContent>
              {hasSearch && crossTabCount > 0 && (
                <Stack align="start" gap={4} className="p-4 pb-8">
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 font-normal underline"
                    onClick={() => handleTabChange(crossTabValue)}
                  >
                    {crossTabCount} result{crossTabCount !== 1 ? "s" : ""} in{" "}
                    {crossTabLabel}
                  </Button>
                </Stack>
              )}
            </PageMainStack>
          </PageMainCard>
        </Tabs>
      </PageContainer>
      <AddReferralModal
        businessId={businessId}
        open={addReferralModalOpen}
        onOpenChange={setAddReferralModalOpen}
        onSuccess={(data) => {
          updateSidebar({
            section: activeTab as BusinessSettingsSidebarSection,
            mode: SIDEBAR_MODES.view,
            selectedItemId: data.potential_customer?.id,
            isOpen: true,
          });
        }}
      />
    </>
  );
};

export default Customers;
